import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { SearchableSelect } from '@components/ui/searchable-select';
import { Textarea } from '@components/ui/textarea';
import React, { useCallback, useEffect, useState } from 'react';

import type { SelectOption } from '@/types/formSchema';

interface ActionInputDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (input: string, tag?: string) => void;
  title: string;
  description?: string;
  inputLabel: string;
  inputPlaceholder?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  isLoading?: boolean;
  required?: boolean;
  options?: SelectOption[];
  defaultTag?: string;
}

export function ActionInputDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  inputLabel,
  inputPlaceholder,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  isLoading = false,
  required = false,
  options,
  defaultTag,
}: ActionInputDialogProps) {
  const [selectedOption, setSelectedOption] = useState(defaultTag ?? '');
  const [inputValue, setInputValue] = useState('');

  const hasOptions = Boolean(options && options.length > 0);
  const isConfirmDisabled =
    isLoading || (required && !inputValue.trim()) || (hasOptions && !selectedOption);

  const handleConfirm = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (required && !inputValue.trim()) {
        return;
      }
      if (hasOptions && !selectedOption) {
        return;
      }
      onConfirm(inputValue.trim(), selectedOption || undefined);
      setSelectedOption(defaultTag ?? '');
      setInputValue('');
    },
    [selectedOption, inputValue, onConfirm, required, hasOptions, defaultTag],
  );

  const handleClose = useCallback(() => {
    setSelectedOption('');
    setInputValue('');
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm],
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedOption(defaultTag ?? '');
    }
  }, [isOpen, defaultTag]);

  return (
    <Dialog open={isOpen} onOpenChange={isOpen => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[500px]" onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {options && options.length > 0 && (
            <div className="grid gap-2">
              <label htmlFor="action-select" className="text-sm font-medium">
                Ineligibility Reason
                {required && <span className="text-destructive ml-1">*</span>}
              </label>
              <SearchableSelect
                id="action-select"
                value={selectedOption}
                onValueChange={value => setSelectedOption(String(value))}
                options={options}
                placeholder="Select a reason..."
                disabled={isLoading}
              />
            </div>
          )}
          <div className="grid gap-2">
            <label htmlFor="action-input" className="text-sm font-medium">
              {inputLabel}
              {required && <span className="text-destructive ml-1">*</span>}
            </label>
            <Textarea
              id="action-input"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={inputPlaceholder}
              className="min-h-[120px]"
              disabled={isLoading}
              autoFocus
            />
            {required && !inputValue.trim() && (
              <p className="text-sm text-muted-foreground">This field is required</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {cancelButtonText}
          </Button>
          <Button onClick={handleConfirm} disabled={isConfirmDisabled}>
            {isLoading ? 'Processing...' : confirmButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
